import { SearchField, GroupedUsers, UserSelectorDialog } from '@halo/ui-chat-react';
import type { SessionStoreInstance } from '@halo/chat-sdk-react';
import type { RefObject } from 'react';
import { useGroupMembers, useStaticAppStore } from '@/store/client';
import { genInviteMessage, useSearch } from './helpers';

interface GroupInviteDialogProps {
  checkUserKey: boolean;
  sessionStoreInstance: SessionStoreInstance | null;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onOK: (
    selected: any[],
    groupInviteDialogRef: RefObject<{
      send: (chat_id: string, chat_name: string, share_url: string) => Promise<void>;
    } | null> | null
  ) => Promise<void>;
  onClose?: () => void;
}

const GroupInviteDialog = ({
  checkUserKey,
  sessionStoreInstance,
  isOpen,
  setIsOpen,
  onOK,
}: GroupInviteDialogProps) => {
  const { client } = useStaticAppStore();
  const { t } = useTranslation();
  const groupInviteDialogRef = useRef<{
    send: (chat_id: string, chat_name: string, share_url: string) => Promise<void>;
  }>(null);

  const [resetTrigger, setResetTrigger] = useState(0);
  const [searchValue, setSearchValue] = useState('');

  const session = useMemo(() => {
    return sessionStoreInstance?.getState()?.session;
  }, [sessionStoreInstance]);

  const { list } = useSearch(searchValue, resetTrigger, isOpen);
  const { members, run } = useGroupMembers(session?.chat_id);

  const listFormatted = useMemo(() => {
    return list.map(({ items, label }) => {
      const filteredItems = items?.map((item) => {
        const isMember = members.find((member) => member.user_id === item.user_id);
        return isMember ? { ...item, disabled: true, defaultChecked: true } : item;
      });
      return { label, items: filteredItems };
    });
  }, [list, members]);

  const {
    // listFlat,
    selected,
    // canSelectList,
    // isSelectedAll,
    toggleSelectUser,
    // onSelectAllChange,
    onRemove,
    resetDialogState,
  } = UserSelectorDialog.useUserSelector(listFormatted, checkUserKey);

  const sendInviteMessage = useCallback(
    async (sendChatId: string, chat_name: string, share_url: string) => {
      // update translate fn
      const payload = genInviteMessage(
        t('chat.groupShareLink', { groupName: chat_name, shareUrl: ' ' }),
        share_url
      );
      await client?.Api.messages.sendMsg({
        chat_id: sendChatId,
        payload,
      });
    },
    [client, t]
  );

  useImperativeHandle(
    groupInviteDialogRef,
    () => ({
      send: sendInviteMessage,
    }),
    [sendInviteMessage]
  );

  useLayoutEffect(() => {
    if (!isOpen) {
      resetDialogState();
      setSearchValue('');
      setResetTrigger((prev) => prev + 1);
    }
  }, [isOpen, resetDialogState]);

  useEffect(() => {
    if (isOpen) {
      return run();
    }
  }, [isOpen, run]);

  return (
    <UserSelectorDialog.Root isOpen={isOpen} setIsOpen={setIsOpen}>
      <UserSelectorDialog.Header title={t('action.addMembers')} />
      <UserSelectorDialog.Body
        selectedList={
          <UserSelectorDialog.SelectedList
            selected={selected}
            onRemove={onRemove}
            checkUserKey={checkUserKey}
          />
        }
      >
        <SearchField searchValue={searchValue} onChange={setSearchValue} />
        <GroupedUsers
          list={listFormatted}
          toggleSelectUser={toggleSelectUser}
          selected={selected}
          checkUserKey={checkUserKey}
        />
        {/* {listFlat.length > 0 && canSelectList.length > 0 && (
          <UserSelectorDialog.SelectAll isChecked={isSelectedAll} onChange={onSelectAllChange} />
        )} */}
      </UserSelectorDialog.Body>
      <UserSelectorDialog.Footer
        selected={selected}
        onOK={async () => await onOK(selected, groupInviteDialogRef)}
        cancelText={t('buttons.cancel')}
        okText={t('buttons.invite')}
      />
    </UserSelectorDialog.Root>
  );
};

const MemoizedGroupInviteDialog = memo(GroupInviteDialog);

export { MemoizedGroupInviteDialog as GroupInviteDialog };
